import { useState, useEffect, useRef } from "react";
import { Offcanvas, Row, Col, Form, Button } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { FaGripLinesVertical, FaSearchPlus, FaSearchMinus, FaCompress, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { format, parseISO } from "date-fns";
import { Document, Page } from "react-pdf";
import { useToast } from "../Context/useToast";
import "./PdfModal.css";
import useElementWidthObserver from "./useElementWidthObserver";

// Use the built-in worker handler from react-pdf
// pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

//pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PdfModal = ({ imageData, propScale, docObj, onCloseModal, rightOffset = 0 }) => {
   const [isLoading, setIsLoading] = useState(true);
   const { triggerToast } = useToast();
   const [scale, setScale] = useState(propScale || 1.0);
   const [pageNo, setPageNo] = useState(1);

   const [numPages, setNumPages] = useState(null);
   const [currentPage, setCurrentPage] = useState(1);
   const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
   const [canvasOffset, setCanvasOffset] = useState({ left: 0, top: 0 });
   const offcanvasRef = useRef(null); //Ref to the Offcanvas
   const canvasRef = useRef(null); //Ref to the canvas (pdf)
   const scrollRef = useRef(0);

   const displayedPages = calculateDisplayedPages(currentPage, numPages);
   const [show, setShow] = useState(true);
   const [isDragging, setIsDragging] = useState(false);
   const dragRef = useRef(null);

   //default width when offcanvas opens
   const [width, setWidth] = useState(window.innerWidth * 0.6);

   useEffect(() => {
      if (canvasRef.current) {
         const offcanvasElement = document.querySelector(".offcanvas.show"); // Get the Offcanvas element manually
         const canvasElement = canvasRef.current.getBoundingClientRect();

         if (!offcanvasElement) {
            console.warn("Offcanvas element not found!");
            return;
         }

         const offcanvasRect = offcanvasElement.getBoundingClientRect();
         const canvasLeft = canvasElement.left; // PDF starting position inside Offcanvas

         const offcanvasLeft = offcanvasRect.left; // Get Offcanvas starting position
         const offcanvasWidth = width;

         const pdfWidth = (pageDimensions.width || 0) * scale; // Adjusted PDF width with zoom

         if (offcanvasWidth === 0 || pdfWidth === 0) {
            console.warn("Missing dimensions! Offcanvas or PDF width is 0.");
            return;
         }

         const centreOffset = (offcanvasWidth - pdfWidth) / 2; // Adjust for centering
         const pdfLeftOffset = canvasLeft - offcanvasLeft; // Corrected PDF start position

         setCanvasOffset({
            left: centreOffset,
            top: canvasRef.current.offsetTop || 0,
            pdfLeftOffset: pdfLeftOffset, // Now scales with zoom
         });
      }
   }, [scale, pageDimensions, width]);

   const onClose = (e) => {
      setShow(false);
      onCloseModal?.();
   };

   const handleMouseDown = () => {
      setIsDragging(true);
      document.body.classList.add("bc-dragging");
   };

   const handleMouseMove = (e) => {
      // Cancel any pending RAF to avoid multiple updates
      if (dragRef.current) cancelAnimationFrame(dragRef.current);

      // Use RAF for smoother animation
      dragRef.current = requestAnimationFrame(() => {
         const newWidth = Math.min(Math.max(200, window.innerWidth - e.clientX), window.innerWidth * 0.8);
         setWidth(newWidth);
      });
   };

   const handleMouseUp = () => {
      setIsDragging(false);
      document.body.classList.remove("bc-dragging");
      // Ensure cleanup of the RAF reference
      if (dragRef.current) {
         cancelAnimationFrame(dragRef.current);
         dragRef.current = null;
      }
   };

   useEffect(() => {
      if (isDragging) {
         window.addEventListener("mousemove", handleMouseMove);
         window.addEventListener("mouseup", handleMouseUp);
      } else {
         window.removeEventListener("mousemove", handleMouseMove);
         window.removeEventListener("mouseup", handleMouseUp);
      }
      return () => {
         window.removeEventListener("mousemove", handleMouseMove);
         window.removeEventListener("mouseup", handleMouseUp);
      };
   }, [isDragging]);

   // Ensure the width is always 50% when the component mounts
   useEffect(() => {
      const handleResize = () => {
         setWidth(window.innerWidth * 0.5); // Adjust to 50% of the viewport
      };

      window.addEventListener("resize", handleResize);
      return () => {
         window.removeEventListener("resize", handleResize);
      };
   }, []);

   const zoomIn = () => setScale(scale + 0.2);
   const zoomOut = () => setScale(scale - 0.2);
   const actualSize = () => setScale(1.0);
   const handlePageChange = (pageNumber) => {
      setCurrentPage(pageNumber);
      setPageNo(pageNumber);
   };

   const handleDocumentLoadSuccess = (pdf) => {
      setNumPages(pdf.numPages);
      setCurrentPage(pageNo);

      // Fetch the first page to get its dimensions
      pdf.getPage(pageNo).then((page) => {
         const { view } = page;
         setPageDimensions({
            width: view[2], // Width of the page
            height: view[3], // Height of the page
         });

         console.log("PDF Loaded:", {
            numPages: pdf.numPages,
            width: view[2],
            height: view[3],
         });
      });
   };

   const handleScroll = (event) => {
      scrollRef.current = event.target.scrollTop; // Store scroll position instantly
   };

   console.log("Re-rendering component at", new Date().toISOString());

   return (
      <>
         <Offcanvas
            onEntered={() => {
               if (offcanvasRef.current) {
                  const offcanvasElement = document.querySelector(".offcanvas.show");
                  if (offcanvasElement) {
                     const offcanvasLeft = offcanvasElement.getBoundingClientRect().left; // Get absolute left position
                     const offcanvasWidth = offcanvasElement.offsetWidth; // Get width of Offcanvas

                     setCanvasOffset((prev) => ({
                        ...prev,
                        offcanvasWidth: offcanvasWidth || 0,
                        offcanvasLeft: offcanvasLeft || 0, // Store the starting position of Offcanvas
                     }));
                     console.log("Offcanvas Width:", offcanvasWidth);
                     console.log("Offcanvas Left:", offcanvasLeft || 0);
                  }
               }
            }}
            style={{
               width: `${width}px`,
               position: "fixed", // Keep fixed positioning
               minWidth: "980px", // set to a minimum
               top: 0,
               right: rightOffset,
               height: "100vh",
               overflow: "hidden",
            }}
            ref={offcanvasRef}
            className=""
            onHide={onClose}
            show={show}
            scroll={true}
            backdrop={false}
            placement="end">
            <div
               className="draggable-grip"
               style={{
                  position: "absolute",
                  top: "50%",
                  left: "-13px", // Align slightly outside the offcanvas for visibility
                  transform: "translateY(-50%)",
                  cursor: "ew-resize",
                  zIndex: 1055, // Ensure it's above the offcanvas content
               }}
               onMouseDown={handleMouseDown}>
               <FaGripLinesVertical size={24} />
            </div>
            <Offcanvas.Header className="me-3" closeButton></Offcanvas.Header>
            <Offcanvas.Body
               style={{
                  overflow: "hidden", // Prevent overflow on the Offcanvas.Body
                  paddingTop: "0px",
               }}>
               <div>
                  <Row>
                     <Col md={12}>
                        <div className="d-flex justify-content-between mb-3">
                           <div>
                              <FaSearchPlus size={22} onClick={zoomIn} title="Zoom In" style={{ cursor: "pointer" }} className="me-3" />
                              <FaSearchMinus size={22} onClick={zoomOut} title="Zoom Out" style={{ cursor: "pointer" }} className="me-3" />
                              <FaCompress size={22} onClick={actualSize} title="Actual Size" style={{ cursor: "pointer" }} />
                           </div>
                           <div className="d-flex flex-column justify-content-center align-items-center text-center">
                              <div className="text-muted">
                                 <small>Uploaded On</small> {docObj?.uploadOn ? format(new Date(docObj.uploadOn), "MMM dd, yyyy") : "N/A"} <small>by</small> {docObj?.uploadBy || "Unknown"}
                              </div>
                              <div className="text-muted">
                                 <small>File</small> {docObj?.name || "N/A"}
                              </div>
                           </div>
                           <div className="d-flex align-items-center">
                              <button
                                 className="btn btn-link fs-5 pe-1"
                                 onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                 disabled={currentPage === 1} // Disable on first page or we have override form
                              >
                                 <FaArrowUp size={20} />
                              </button>
                              <input
                                 type="number"
                                 className="form-control text-center"
                                 value={currentPage}
                                 onChange={(e) => {
                                    let newPage = e.target.value; // Allow temporary invalid values
                                    if (newPage === "" || (!isNaN(newPage) && newPage >= 1 && newPage <= numPages)) {
                                       setCurrentPage(newPage);
                                    }
                                 }}
                                 onBlur={(e) => {
                                    let newPage = parseInt(e.target.value, 10);
                                    if (isNaN(newPage) || newPage < 1) {
                                       setCurrentPage(1); // Reset to 1 if below range
                                    } else if (newPage > numPages) {
                                       setCurrentPage(numPages); // Reset to last page if above range
                                    } else {
                                       setCurrentPage(newPage);
                                    }
                                 }}
                                 onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                       let newPage = parseInt(e.target.value, 10);
                                       if (!isNaN(newPage) && newPage >= 1 && newPage <= numPages) {
                                          handlePageChange(newPage);
                                          e.target.blur();
                                       }
                                    }
                                 }}
                                 style={{ width: "50px", margin: "0 5px" }}
                              />
                              <span className="text-muted"> of {numPages}</span>
                              <button
                                 className="btn btn-link fs-5 pe-1"
                                 onClick={() => handlePageChange(Math.min(numPages, currentPage + 1))}
                                 disabled={currentPage === numPages} // Disable on last page
                              >
                                 <FaArrowDown size={20} />
                              </button>
                           </div>
                        </div>
                        <div
                           onScroll={handleScroll}
                           className="d-flex flex-column align-items-center pt-4 "
                           style={{
                              overflowY: "auto", // Enable vertical scrolling
                              overflowX: "hidden", // Disable horizontal scrolling
                              maxHeight: `calc(100vh - 100px)`, // Adjust this value to account for any header, footer, or controls
                              width: "100%", // Ensure the div spans the full width of its container
                              clipPath: "inset(0px 0px 0px 0px)", // Prevents the box from leaking out
                           }}>
                           <div ref={canvasRef} style={{ position: "relative", display: "inline-block" }}>
                              <Document file={`data:application/pdf;base64,${imageData}`} onLoadSuccess={handleDocumentLoadSuccess}>
                                 <Page
                                    pageNumber={pageNo}
                                    scale={scale}
                                    renderTextLayer={false} // Disable text layer
                                    renderAnnotationLayer={false} // Disable annotation layer
                                    style={{ width: "100%" }}
                                 />
                              </Document>
                           </div>
                        </div>
                     </Col>
                  </Row>
               </div>
            </Offcanvas.Body>
         </Offcanvas>
      </>
   );
};

function calculateDisplayedPages(currentPage, numPages, maxDisplayedPages = 1000) {
   if (!numPages) return [];

   const range = Math.min(maxDisplayedPages, numPages);
   const half = Math.floor(range / 2);
   let start = Math.max(currentPage - half, 1);
   let end = Math.min(start + range - 1, numPages);

   if (end - start < range - 1) {
      start = Math.max(end - range + 1, 1);
   }

   const pages = [];
   for (let i = start; i <= end; i++) {
      pages.push(i);
   }

   return pages;
}

export default PdfModal;
